import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import DataUtil from '@/util/DataUtil';
import { ESTADO_JULGAMENTO_PROCESSO } from './EstadoJulgamentoProcesso';
import { MEIO_JULGAMENTO_PROCESSO } from './MeioJulgamentoProcesso';
import { MEIO_JULGAMENTO_SESSAO } from './MeioJulgamentoSessao';
import TipoRemessaJulgamentoProcesso, { TIPO_REMESSA_JULGAMENTO_PROCESSO } from './TipoRemessaJulgamentoProcesso';
import { RESULTADO_JULGAMENTO } from './ResultadoJulgamento';
dayjs.extend(customParseFormat);

/**
 * @classdesc Agrega as regras semânticas relacionadas às situações de processo.
 * @readonly
 */
export default class SituacaoJulgamentoProcessoBusiness {
    static codigosProcessoJulgado = new Set([
        RESULTADO_JULGAMENTO.JULGADO.codigo,
        RESULTADO_JULGAMENTO.JULGADO_OBSERVACAO.codigo,
        RESULTADO_JULGAMENTO.JULGADO_ALTERACAO_VOTO.codigo
    ]);

    /**
     * Verifica se o processo está apreciado.
     *
     * @public
     * @param {Object} resultadoJulgamento Objeto com o resultado de julgamento na consulta de sessão.
     * @returns {boolean} Um sinalizador que indica se o processo está apreciado (true) ou não (false).
     */
    static isProcessoApreciado(resultadoJulgamento) {
        return (
            resultadoJulgamento?.julgado === true ||
            resultadoJulgamento?.julgado === 'true' ||
            (resultadoJulgamento?.codigo && resultadoJulgamento?.codigo !== RESULTADO_JULGAMENTO.NAO_JULGADO.codigo)
        );
    }

    /**
     * Verifica se o processo está julgado.
     *
     * @public
     * @param {Object} resultadoJulgamento Objeto com o resultado de julgamento na consulta de sessão.
     * @returns {boolean} Um sinalizador que indica se o processo está julgado (true) ou não (false).
     */
    static isProcessoJulgado(resultadoJulgamento) {
        return !!(
            (resultadoJulgamento?.codigo && this.codigosProcessoJulgado.has(resultadoJulgamento.codigo)) ||
            resultadoJulgamento?.julgado === true ||
            resultadoJulgamento?.julgado === 'true'
        );
    }

    /**
     * Verifica se o processo está julgado pelo código do resultado de apreciação.
     *
     * @public
     * @param {string} codigoResultadoJulgamento O código do resultado de apreciação.
     * @returns {boolean} Um sinalizador que indica se o processo está julgado (true) ou não (false).
     */
    static isProcessoJulgadoByCodigo(codigoResultadoJulgamento) {
        return !!(codigoResultadoJulgamento && this.codigosProcessoJulgado.has(codigoResultadoJulgamento));
    }

    /**
     * Verifica se o processo está remetido (destacado).
     *
     * @public
     * @param {string} codigoMeioJulgamentoProcesso Código do meio de julgamento do processo.
     * @param {Object} remessaJulgamento Código do meio de julgamento do processo.
     * @returns {boolean} Um sinalizador que indica se o processo está remetido (true) ou não (false).
     */
    static isProcessoRemetido(codigoMeioJulgamentoProcesso, remessaJulgamento) {
        const tiposRemessa = new Set([
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RF.codigo,
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RFP.codigo,
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RFV.codigo,
            TIPO_REMESSA_JULGAMENTO_PROCESSO.RP.codigo
        ]);

        const tiposRemessaLegado = new Set([MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo]);

        const temIndicacaoRemessa = !!(remessaJulgamento?.tipo?.id && tiposRemessa.has(remessaJulgamento.tipo.id));
        const temIndicacaoRemessaLegado = !!(
            codigoMeioJulgamentoProcesso && tiposRemessaLegado.has(codigoMeioJulgamentoProcesso)
        );

        return temIndicacaoRemessa || temIndicacaoRemessaLegado;
    }

    static isRemetidoParaPresencialVinculada(
        codigoMeioJulgamentoProcesso,
        codigoMeioJulgamentoSessao,
        remessaJulgamento
    ) {
        if (codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo) {
            return false;
        }

        const tiposRemessa = new Set([TIPO_REMESSA_JULGAMENTO_PROCESSO.RP.codigo]);
        const tiposRemessaLegado = new Set([MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo]);

        const temIndicacaoRemessa = remessaJulgamento?.tipo?.id && tiposRemessa.has(remessaJulgamento.tipo.id);
        const temIndicacaoRemessaLegado =
            codigoMeioJulgamentoProcesso && tiposRemessaLegado.has(codigoMeioJulgamentoProcesso);

        return (
            (codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo && temIndicacaoRemessa) ||
            temIndicacaoRemessaLegado
        );
    }

    static isProcessoPautadoEmJulgamentoExclusivamenteVirtual(codigoMeioJulgamentoSessao) {
        return codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo;
    }

    static isProcessoRemetidoDoJulgamentoExclusivamenteVirtual(
        codigoMeioJulgamentoProcesso,
        codigoMeioJulgamentoSessao,
        remessaJulgamento
    ) {
        return (
            SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
                codigoMeioJulgamentoSessao
            ) && SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(codigoMeioJulgamentoProcesso, remessaJulgamento)
        );
    }

    static isProcessoPautadoEmJulgamentoVirtual(
        codigoMeioJulgamentoProcesso,
        codigoMeioJulgamentoSessao,
        remessaJulgamento
    ) {
        return (
            SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
                codigoMeioJulgamentoSessao
            ) ||
            codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo ||
            SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(codigoMeioJulgamentoProcesso, remessaJulgamento)
        );
    }

    static isProcessoEmJulgamentoVirtual(codigoMeioJulgamentoProcesso, remessaJulgamento) {
        return (
            codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo &&
            !SituacaoJulgamentoProcessoBusiness.isProcessoRemetido(codigoMeioJulgamentoProcesso, remessaJulgamento)
        );
    }

    static isJulgamentoIniciado(prazosSessao, codigoMeioJulgamentoProcesso, remessaJulgamento) {
        const hoje = dayjs();
        let dataObj;

        if (
            SituacaoJulgamentoProcessoBusiness.isProcessoEmJulgamentoVirtual(
                codigoMeioJulgamentoProcesso,
                remessaJulgamento
            )
        ) {
            dataObj = DataUtil.getObjData(prazosSessao?.dataInicioVotacaoVirtual);
        } else if (prazosSessao?.dataInicioSessaoPresencial) {
            dataObj = DataUtil.getObjData(prazosSessao?.dataInicioSessaoPresencial);
        }

        return !!(hoje && dataObj && hoje.isAfter(dataObj, 'second'));
    }

    static getEstadoJulgamentoProcesso(
        prazosSessao,
        codigoMeioJulgamentoProcesso,
        codigoMeioJulgamentoSessao,
        remessaJulgamento
    ) {
        const hoje = dayjs();

        if (
            SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
                codigoMeioJulgamentoSessao
            )
        ) {
            return SituacaoJulgamentoProcessoBusiness.#getEstadoJulgamentoProcessoExclusivamenteVirtual(
                prazosSessao,
                hoje
            );
        } else if (codigoMeioJulgamentoProcesso === MEIO_JULGAMENTO_PROCESSO.PRESENCIAL.codigo) {
            return SituacaoJulgamentoProcessoBusiness.#getEstadoJulgamentoProcessoExclusivamentePresencial(
                prazosSessao,
                hoje
            );
        } else {
            return SituacaoJulgamentoProcessoBusiness.#getEstadoJulgamentoProcessoPresencialComPeriodoVirtual(
                codigoMeioJulgamentoProcesso,
                prazosSessao,
                remessaJulgamento,
                hoje
            );
        }
    }

    static #getEstadoJulgamentoProcessoExclusivamenteVirtual(prazosSessao, hoje) {
        const inicio = prazosSessao?.dataInicioVotacaoVirtual
            ? DataUtil.getObjData(prazosSessao.dataInicioVotacaoVirtual)
            : null;
        const fim = prazosSessao?.dataFimVotacaoVirtual
            ? DataUtil.getObjData(prazosSessao.dataFimVotacaoVirtual)
            : null;

        if (inicio && hoje.isBefore(inicio)) {
            return ESTADO_JULGAMENTO_PROCESSO.AGUARDANDO_INICIO_JULGAMENTO_VIRTUAL;
        }

        if (inicio && fim && hoje.isAfter(inicio) && hoje.isBefore(fim)) {
            return ESTADO_JULGAMENTO_PROCESSO.EM_JULGAMENTO_VIRTUAL;
        }

        if (fim && hoje.isAfter(fim)) {
            return ESTADO_JULGAMENTO_PROCESSO.JULGAMENTO_EXCLUSIVAMENTE_VIRTUAL_FINALIZADO;
        }

        return ESTADO_JULGAMENTO_PROCESSO.DESCONHECIDO;
    }

    static #getEstadoJulgamentoProcessoExclusivamentePresencial(prazosSessao, hoje) {
        const inicio = prazosSessao?.dataInicioSessaoPresencial
            ? DataUtil.getObjData(prazosSessao.dataInicioSessaoPresencial)
            : null;

        if (inicio && hoje.isBefore(inicio)) {
            return ESTADO_JULGAMENTO_PROCESSO.AGUARDANDO_SESSAO_JULGAMENTO_PRESENCIAL;
        }

        if (inicio && hoje.isAfter(inicio) && hoje.isSame(inicio, 'day')) {
            return ESTADO_JULGAMENTO_PROCESSO.EM_JULGAMENTO_PRESENCIAL;
        }

        if (inicio && hoje.isAfter(inicio, 'day')) {
            return ESTADO_JULGAMENTO_PROCESSO.JULGAMENTO_PRESENCIAL_FINALIZADO;
        }

        return ESTADO_JULGAMENTO_PROCESSO.DESCONHECIDO;
    }

    static #getEstadoJulgamentoProcessoPresencialComPeriodoVirtual(
        codigoMeioJulgamentoProcesso,
        prazosSessao,
        remessaJulgamento,
        hoje
    ) {
        let estado;

        if (
            SituacaoJulgamentoProcessoBusiness.isProcessoEmJulgamentoVirtual(
                codigoMeioJulgamentoProcesso,
                remessaJulgamento
            )
        ) {
            estado = SituacaoJulgamentoProcessoBusiness.#getEstadoJulgamentoProcessoExclusivamenteVirtual(
                prazosSessao,
                hoje
            );

            if (estado.codigo === ESTADO_JULGAMENTO_PROCESSO.JULGAMENTO_EXCLUSIVAMENTE_VIRTUAL_FINALIZADO.codigo) {
                estado = ESTADO_JULGAMENTO_PROCESSO.JULGAMENTO_VIRTUAL_FINALIZADO;
            }
        } else {
            estado = SituacaoJulgamentoProcessoBusiness.#getEstadoJulgamentoProcessoExclusivamentePresencial(
                prazosSessao,
                hoje
            );

            if (estado.codigo === ESTADO_JULGAMENTO_PROCESSO.AGUARDANDO_SESSAO_JULGAMENTO_PRESENCIAL.codigo) {
                estado = ESTADO_JULGAMENTO_PROCESSO.AGUARDANDO_SESSAO_JULGAMENTO_PRESENCIAL_VINCULADA;
            }
        }

        return estado || ESTADO_JULGAMENTO_PROCESSO.DESCONHECIDO;
    }

    static tratarMeioJulgamento(codigoMeioJulgamentoProcesso, codigoMeioJulgamentoSessao, remessaJulgamento) {
        if (
            SituacaoJulgamentoProcessoBusiness.isProcessoPautadoEmJulgamentoExclusivamenteVirtual(
                codigoMeioJulgamentoSessao
            )
        ) {
            return MEIO_JULGAMENTO_PROCESSO.VIRTUAL.codigo;
        }

        if (
            SituacaoJulgamentoProcessoBusiness.isRemetidoParaPresencialVinculada(
                codigoMeioJulgamentoProcesso,
                codigoMeioJulgamentoSessao,
                remessaJulgamento
            )
        ) {
            return MEIO_JULGAMENTO_PROCESSO.REMETIDO_PARA_SESSAO_VINCULADA.codigo;
        }

        return codigoMeioJulgamentoProcesso;
    }

    static tratarSituacaoJulgamento(
        codigoMeioJulgamentoProcesso,
        codigoMeioJulgamentoSessao,
        remessaJulgamento,
        prazosSessao,
        resultadoJulgamento
    ) {
        let descricaoSituacao = '';

        if (this.isProcessoJulgado(resultadoJulgamento)) {
            descricaoSituacao = 'Julgado';
        } else {
            if (resultadoJulgamento?.descricao) {
                descricaoSituacao = resultadoJulgamento?.descricao;
            }

            const descricaoRemessa = SituacaoJulgamentoProcessoBusiness.tratarSituacaoRemessa(
                codigoMeioJulgamentoProcesso,
                codigoMeioJulgamentoSessao,
                remessaJulgamento
            );

            if (descricaoRemessa) {
                descricaoSituacao += descricaoSituacao ? ` (${descricaoRemessa})` : descricaoRemessa;
            }
        }

        if (!descricaoSituacao) {
            const estado = SituacaoJulgamentoProcessoBusiness.getEstadoJulgamentoProcesso(
                prazosSessao,
                codigoMeioJulgamentoProcesso,
                codigoMeioJulgamentoSessao,
                remessaJulgamento
            );

            if (estado.codigo !== ESTADO_JULGAMENTO_PROCESSO.DESCONHECIDO.codigo) {
                descricaoSituacao = estado.descricao;
            }
        }

        return descricaoSituacao;
    }

    static tratarSituacaoRemessa(codigoMeioJulgamentoProcesso, codigoMeioJulgamentoSessao, remessaJulgamento) {
        const idRemessa = remessaJulgamento?.tipo?.id;

        if (idRemessa) {
            // Fallback para casos de registros errados no banco de dados
            if (
                codigoMeioJulgamentoSessao === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo &&
                idRemessa === TIPO_REMESSA_JULGAMENTO_PROCESSO.RP.id
            ) {
                return TIPO_REMESSA_JULGAMENTO_PROCESSO.RF.descricao;
            }

            return TipoRemessaJulgamentoProcesso.getDescricao(idRemessa);
        }

        if (
            SituacaoJulgamentoProcessoBusiness.isRemetidoParaPresencialVinculada(
                codigoMeioJulgamentoProcesso,
                codigoMeioJulgamentoSessao,
                remessaJulgamento
            )
        ) {
            return TIPO_REMESSA_JULGAMENTO_PROCESSO.RP.descricao;
        }

        // Tratamento legado
        if (
            SituacaoJulgamentoProcessoBusiness.isProcessoRemetidoDoJulgamentoExclusivamenteVirtual(
                codigoMeioJulgamentoProcesso,
                codigoMeioJulgamentoSessao,
                remessaJulgamento
            )
        ) {
            return TIPO_REMESSA_JULGAMENTO_PROCESSO.RFP.descricao;
        }

        return undefined;
    }

    static isJulgamentoExclusivamentePresencial(sessao) {
        return (
            sessao?.tipoJulgamento !== MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo &&
            (!sessao?.prazos?.temPrazoDeVotacaoVirtual ||
                sessao?.prazos?.dataInicioVotacaoVirtual === sessao?.prazos?.dataFimVotacaoVirtual ||
                (sessao?.prazos?.dataInicioVotacaoVirtual === null && sessao?.prazos?.dataFimVotacaoVirtual === null))
        );
    }
}
